import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataList,
  Dialog,
  EmptyState,
  PageTitle,
  SelectField,
  pillClass,
  ROOM_SEX_TEXT,
  TextAreaField,
  TextField,
  TEXT_LINK,
} from '@rooming/ui';
import {
  countryOfHost,
  erpReportToCsv,
  fichaTextToCsv,
  formatInZone,
  readXlsxRows,
  XlsxError,
  zonedDateTimeToIso,
} from '@rooming/web-core';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useParams, useSearchParams } from 'react-router';
import {
  backoffice,
  type Group,
  type Hotel,
  type ImportCommit,
  type ImportPreview,
  type Member,
  type NewSigner,
  type Room,
  type RoomAudience,
  type Stay,
  type MemberChanges,
} from '../api';
import { Loaded, PasswordResetDialog, ReasonDialog, SEX_TEXT, STAY_STATUS, useCommand } from '../common';
import { useSession } from '../session';
import { GroupInvitation } from './GroupInvitation';
import { AddPassengerDialog } from './GuidePassengers';
import { RoomReplacementDialog } from './RoomReplacementDialog';
import { StayBoard } from './StayBoard';

const TABS = [
  ['resumen', 'Resumen'],
  ['integrantes', 'Integrantes'],
  ['habitaciones', 'Habitaciones'],
  ['distribucion', 'Distribución'],
  ['importaciones', 'Importaciones'],
] as const;

/** The group file: the center of the daily work (BACKOFFICE_DESIGN §1.1). One tab per task; the stay is shared. */
export function GroupPage() {
  const { groupId = '' } = useParams();
  const group = useQuery({ queryKey: ['group', groupId], queryFn: () => backoffice.group(groupId) });
  const stays = useQuery({ queryKey: ['stays', groupId], queryFn: () => backoffice.stays({ groupId }) });
  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels });
  const [params, setParams] = useSearchParams();
  const selected = stays.data?.find((s) => s.id === params.get('estadia')) ?? stays.data?.[0];
  const hotelOf = (stay: Stay): Hotel | undefined => hotels.data?.find((h) => h.id === stay.hotelId);
  const search = selected === undefined ? '' : `?estadia=${selected.id}`;

  return (
    <Loaded query={group}>
      {(g) => (
        <>
          <div className="mb-3">
            <Link to="/grupos" className={TEXT_LINK}>
              ← Grupos
            </Link>
          </div>
          <PageTitle subtitle={<span className="font-mono">{g.code}</span>}>{g.name}</PageTitle>
          <nav aria-label="Secciones del grupo" className="mb-5 flex flex-wrap gap-2 border-b border-line pb-3">
            {TABS.map(([path, text]) => (
              <NavLink
                key={path}
                to={`/grupos/${groupId}/${path}${search}`}
                className={({ isActive }) => pillClass(isActive)}
              >
                {text}
              </NavLink>
            ))}
          </nav>
          {(stays.data?.length ?? 0) > 1 && (
            <div className="mb-5 max-w-md">
              <SelectField
                label="Estadía"
                options={(stays.data ?? []).map((s) => ({
                  value: s.id,
                  label: `${hotelOf(s)?.name ?? 'Hotel'} · ${formatInZone(s.checkInAt, hotelOf(s)?.timezone ?? 'UTC')}`,
                }))}
                value={selected?.id ?? ''}
                onChange={(event) => {
                  setParams({ estadia: event.target.value });
                }}
              />
            </div>
          )}
          <Routes>
            <Route index element={<Navigate to={`/grupos/${groupId}/resumen${search}`} replace />} />
            <Route
              path="resumen"
              element={<SummaryTab group={g} stays={stays.data ?? []} hotels={hotels.data ?? []} />}
            />
            <Route path="integrantes" element={<MembersTab groupId={groupId} />} />
            <Route
              path="habitaciones"
              element={<NeedsStay stay={selected}>{(stay) => <RoomsTab stay={stay} />}</NeedsStay>}
            />
            <Route
              path="distribucion"
              element={
                <NeedsStay stay={selected}>
                  {(stay) => <DistributionTab stay={stay} timeZone={hotelOf(stay)?.timezone ?? 'UTC'} />}
                </NeedsStay>
              }
            />
            <Route path="importaciones" element={<ImportsTab groupId={groupId} />} />
          </Routes>
        </>
      )}
    </Loaded>
  );
}

function NeedsStay({
  stay,
  children,
}: {
  readonly stay: Stay | undefined;
  readonly children: (stay: Stay) => React.ReactNode;
}) {
  if (stay === undefined)
    return <EmptyState title="El grupo todavía no tiene estadías">Creá una en la pestaña Resumen.</EmptyState>;
  return <>{children(stay)}</>;
}

// ── The administrator's message for the hotel of one stay (owner, 2026-10-07) ──

function HotelMessageDialog({
  stay,
  hotelName,
  onClose,
}: {
  readonly stay: Stay;
  readonly hotelName: string;
  readonly onClose: () => void;
}) {
  const current = useQuery({ queryKey: ['hotel-message', stay.id], queryFn: () => backoffice.hotelMessage(stay.id) });
  const [draft, setDraft] = useState<string>();
  const { busy, run } = useCommand();
  const saved = current.data?.text ?? '';
  const text = draft ?? saved;
  const save = (value: string, done: string): void => {
    void run(() => backoffice.setHotelMessage(stay.id, value), done, [['hotel-message', stay.id]]).then((ok) => {
      if (ok) onClose();
    });
  };
  return (
    <Dialog
      open
      title={`Mensaje para ${hotelName}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          {saved !== '' && (
            <Button
              variant="secondary"
              loading={busy}
              onClick={() => {
                save('', 'Mensaje borrado.');
              }}
            >
              Borrar mensaje
            </Button>
          )}
          <Button
            loading={busy}
            disabled={current.isPending || text.trim() === '' || text.trim() === saved}
            onClick={() => {
              save(text, 'Mensaje guardado. El hotel lo ve en su página y lo recibe por correo.');
            }}
          >
            Guardar y avisar al hotel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-base text-muted">
          Lo que escribas aparece arriba de la distribución que ve el hotel. Al guardarlo, el hotel recibe una
          notificación y un correo (a los correos cargados en Hoteles).
        </p>
        <TextAreaField
          label="Mensaje"
          rows={6}
          maxLength={2000}
          hint={`${String(text.length)} de 2000 caracteres. Sin datos de documentos.`}
          value={text}
          disabled={current.isPending}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
      </div>
    </Dialog>
  );
}

// ── Resumen: stays of the group, create a stay, open the selection ──

function SummaryTab({
  group,
  stays,
  hotels,
}: {
  readonly group: Group;
  readonly stays: readonly Stay[];
  readonly hotels: readonly Hotel[];
}) {
  const groupId = group.id;
  const { hasRole } = useSession();
  const canWrite = hasRole('TENANT_ADMIN', 'HOTEL_ADMIN');
  // prompt11 §15, §20: the exchange switch of the group and closing a stay are an administrator's.
  const isAdmin = hasRole('TENANT_ADMIN', 'HOTEL_ADMIN');
  const { busy, run } = useCommand();
  const [opening, setOpening] = useState<Stay>();
  const [closing, setClosing] = useState<Stay>();
  const [messaging, setMessaging] = useState<Stay>();
  const [switching, setSwitching] = useState(false);
  // What this tab offers depends on each stay's status, which changes elsewhere (the signatory confirms in another
  // application): the list is re-read every time the tab is opened.
  const queryClient = useQueryClient();
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['stays', groupId] });
  }, [queryClient, groupId]);
  const [form, setForm] = useState({ hotelId: '', checkIn: '', checkOut: '' });
  const hotel = hotels.find((h) => h.id === form.hotelId);

  async function create(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    if (hotel === undefined) return;
    const checkInAt = zonedDateTimeToIso(form.checkIn, hotel.timezone);
    const checkOutAt = zonedDateTimeToIso(form.checkOut, hotel.timezone);
    if (checkInAt === undefined || checkOutAt === undefined) return;
    const ok = await run(
      () => backoffice.createStay({ groupId, hotelId: hotel.id, checkInAt, checkOutAt }),
      'Estadía creada.',
      [['stays']],
    );
    if (ok) setForm({ hotelId: '', checkIn: '', checkOut: '' });
  }

  return (
    <div className="flex flex-col gap-5">
      {stays
        .filter((s) => s.status !== 'CLOSED' && hotels.find((h) => h.id === s.hotelId)?.contactEmails.length === 0)
        .map((s) => (
          <Alert key={s.id} tone="warning" title="Este hotel no tiene correo cargado">
            Al confirmar la distribución, a {hotels.find((h) => h.id === s.hotelId)?.name ?? 'el hotel'} no le llegará
            ningún correo. Cargale uno en Hoteles, o descargá el rooming list y enviáselo vos. La confirmación no se
            bloquea.
          </Alert>
        ))}
      <DataList<Stay>
        caption="Estadías del grupo"
        rows={stays}
        rowKey={(s) => s.id}
        empty="El grupo todavía no tiene estadías."
        columns={[
          { header: 'Hotel', cell: (s) => hotels.find((h) => h.id === s.hotelId)?.name ?? '—' },
          {
            header: 'Entrada (hora del hotel)',
            cell: (s) => formatInZone(s.checkInAt, hotels.find((h) => h.id === s.hotelId)?.timezone ?? 'UTC'),
          },
          {
            header: 'Salida (hora del hotel)',
            cell: (s) => formatInZone(s.checkOutAt, hotels.find((h) => h.id === s.hotelId)?.timezone ?? 'UTC'),
          },
          {
            header: 'Estado',
            cell: (s) => (
              <Badge tone={STAY_STATUS[s.status]?.tone ?? 'neutral'}>{STAY_STATUS[s.status]?.text ?? s.status}</Badge>
            ),
          },
          {
            // Owner, 2026-10-07: a free-text message the hotel reads on its page (and receives by email).
            header: 'Mensaje al hotel',
            cell: (s) =>
              canWrite ? (
                <Button
                  variant="secondary"
                  aria-label={`Mensaje para ${hotels.find((h) => h.id === s.hotelId)?.name ?? 'el hotel'}`}
                  onClick={() => {
                    setMessaging(s);
                  }}
                >
                  Mensaje al hotel
                </Button>
              ) : (
                '—'
              ),
          },
          {
            header: 'Acción',
            end: true,
            cell: (s) =>
              canWrite && s.status === 'DRAFT' ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setOpening(s);
                  }}
                >
                  Abrir selección
                </Button>
              ) : isAdmin && ['OPEN', 'REOPENED', 'CONFIRMED'].includes(s.status) ? (
                <Button
                  variant="secondary"
                  aria-label={`Cerrar la estadía de ${hotels.find((h) => h.id === s.hotelId)?.name ?? 'el hotel'}`}
                  onClick={() => {
                    setClosing(s);
                  }}
                >
                  Cerrar estadía
                </Button>
              ) : (
                '—'
              ),
          },
        ]}
      />
      <GroupInvitation groupId={group.id} />
      {group.swapsAvailable !== false && (
        <Card className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Intercambios entre pasajeros</h2>
          <p className="text-base">
            En este grupo:{' '}
            <Badge tone={group.swapsEnabled ? 'success' : 'neutral'}>
              {group.swapsEnabled ? 'Habilitados' : 'Deshabilitados'}
            </Badge>
          </p>
          <p className="text-base text-muted">
            Con los intercambios habilitados, un pasajero puede pedirle a otro cambiar de plaza; se hace solo si el otro
            acepta dentro de las 24 horas. Vale solo para este grupo.
          </p>
          {hasRole('TENANT_ADMIN') && (
            <div>
              <Button
                variant={group.swapsEnabled ? 'secondary' : 'primary'}
                onClick={() => {
                  setSwitching(true);
                }}
              >
                {group.swapsEnabled ? 'Deshabilitar intercambios' : 'Habilitar intercambios'}
              </Button>
            </div>
          )}
        </Card>
      )}
      <ReasonDialog
        open={switching}
        title={
          group.swapsEnabled ? '¿Deshabilitar los intercambios del grupo?' : '¿Habilitar los intercambios del grupo?'
        }
        confirmLabel={group.swapsEnabled ? 'Deshabilitar' : 'Habilitar'}
        danger={group.swapsEnabled}
        busy={busy}
        onConfirm={(reason) => {
          void run(
            () => backoffice.setGroupExchanges(groupId, !group.swapsEnabled, reason),
            group.swapsEnabled
              ? 'Intercambios deshabilitados para el grupo.'
              : 'Intercambios habilitados para el grupo.',
            [['group', groupId], ['groups']],
          ).then((ok) => {
            if (ok) setSwitching(false);
          });
        }}
        onClose={() => {
          setSwitching(false);
        }}
      >
        <p>
          {group.swapsEnabled
            ? 'Los pasajeros de este grupo ya no podrán pedir ni aceptar intercambios. Las solicitudes pendientes vencen solas.'
            : 'Los pasajeros de este grupo podrán pedir intercambios; cada solicitud vence a las 24 horas.'}
        </p>
      </ReasonDialog>
      {messaging !== undefined && (
        <HotelMessageDialog
          stay={messaging}
          hotelName={hotels.find((h) => h.id === messaging.hotelId)?.name ?? 'el hotel'}
          onClose={() => {
            setMessaging(undefined);
          }}
        />
      )}
      <ReasonDialog
        open={closing !== undefined}
        title="¿Cerrar la estadía?"
        confirmLabel="Cerrar estadía"
        danger
        busy={busy}
        onConfirm={(reason) => {
          if (closing === undefined) return;
          void run(() => backoffice.closeStay(closing.id, reason), 'Estadía cerrada.', [
            ['stays'],
            ['board', closing.id],
            ['distribution', closing.id],
          ]).then((ok) => {
            if (ok) setClosing(undefined);
          });
        }}
        onClose={() => {
          setClosing(undefined);
        }}
      >
        <p>
          Se cierra cuando el viaje terminó, esté o no confirmada la distribución. Es definitivo: después no cambia la
          distribución, las habitaciones ni los números del hotel. Todo sigue disponible para consulta y descarga.
        </p>
      </ReasonDialog>
      {hasRole('TENANT_ADMIN') && (
        <Card>
          <h2 className="mb-3 text-lg font-semibold">Nueva estadía</h2>
          <form className="grid gap-4 md:grid-cols-3" onSubmit={(event) => void create(event)}>
            <SelectField
              label="Hotel"
              placeholder="Elegí un hotel"
              options={hotels
                .filter((h) => h.status === 'ACTIVE')
                .map((h) => ({ value: h.id, label: `${h.name} (${h.city})` }))}
              required
              value={form.hotelId}
              onChange={(event) => {
                setForm({ ...form, hotelId: event.target.value });
              }}
            />
            <TextField
              label="Entrada"
              type="datetime-local"
              hint={hotel === undefined ? 'En la hora del hotel.' : `Hora de ${hotel.city} (${hotel.timezone}).`}
              required
              value={form.checkIn}
              onChange={(event) => {
                setForm({ ...form, checkIn: event.target.value });
              }}
            />
            <TextField
              label="Salida"
              type="datetime-local"
              hint={hotel === undefined ? 'En la hora del hotel.' : `Hora de ${hotel.city} (${hotel.timezone}).`}
              required
              value={form.checkOut}
              onChange={(event) => {
                setForm({ ...form, checkOut: event.target.value });
              }}
            />
            <div className="md:col-span-3">
              <Button
                type="submit"
                loading={busy}
                disabled={form.hotelId === '' || form.checkIn === '' || form.checkOut === ''}
              >
                Crear estadía
              </Button>
            </div>
          </form>
        </Card>
      )}
      <Dialog
        open={opening !== undefined}
        title="¿Abrir la selección de habitaciones?"
        onClose={() => {
          if (!busy) setOpening(undefined);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setOpening(undefined);
              }}
            >
              Cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() => {
                if (opening === undefined) return;
                void run(() => backoffice.openStay(opening.id), 'Selección abierta.', [['stays']]).then(() => {
                  setOpening(undefined);
                });
              }}
            >
              Abrir selección
            </Button>
          </>
        }
      >
        <p>Los pasajeros del grupo podrán elegir su plaza. Revisá antes que las habitaciones alcancen para todos.</p>
      </Dialog>
    </div>
  );
}

// ── Integrantes ──

function MembersTab({ groupId }: { readonly groupId: string }) {
  const { hasRole } = useSession();
  const members = useQuery({ queryKey: ['members', groupId], queryFn: () => backoffice.members(groupId) });
  const { busy, run } = useCommand();
  const [cancelling, setCancelling] = useState<Member>();
  const [designating, setDesignating] = useState<Member>();
  const [editing, setEditing] = useState<Member>();
  const [resetting, setResetting] = useState<Member>();
  const canWrite = hasRole('TENANT_ADMIN', 'HOTEL_ADMIN');
  // prompt10 §4, §14, §15: the FIRMANTE, passenger data and passwords are an administrator's.
  const isAdmin = hasRole('TENANT_ADMIN', 'HOTEL_ADMIN');
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState<{ name: string; username: string | null }>();
  // Owner, 2026-10-29: when the group link is closed (or someone is missing) an administrator registers a person by hand.
  const [addingPassenger, setAddingPassenger] = useState(false);
  const [addedPassenger, setAddedPassenger] = useState<{ name: string; username: string }>();
  const queryClient = useQueryClient();
  const hasSigner = (members.data ?? []).some((m) => m.isSigner && m.status === 'ACTIVE');
  // D-6: find a member in a long list (by name; accents and case ignored).
  const [search, setSearch] = useState('');
  const fold = (text: string): string => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const matches = (m: Member): boolean => fold(`${m.firstNames} ${m.lastNames}`).includes(fold(search.trim()));
  return (
    <>
      <div className="mb-4 max-w-md">
        <TextField
          label="Buscar integrante por nombre"
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </div>
      {members.data !== undefined && !hasSigner && (
        <div className="mb-4">
          <Alert tone="warning" title="Grupo bloqueado: falta el firmante">
            Un grupo no opera sin su firmante: no se puede abrir la selección ni confirmar la distribución. Viene en el
            archivo de importación con el rol «firmante», o lo registra un administrador acá.
          </Alert>
        </div>
      )}
      {registered !== undefined && (
        <div className="mb-4">
          <Alert tone="success" title={`${registered.name} es el firmante del grupo`}>
            Usuario: <span className="font-mono">{registered.username ?? '—'}</span>. Su primera contraseña es su número
            de documento; el sistema le pide elegir una propia al ingresar. Entregale estos datos por un canal seguro.
          </Alert>
        </div>
      )}
      {addedPassenger !== undefined && (
        <div className="mb-4">
          <Alert tone="success" title={`${addedPassenger.name} quedó registrado`}>
            Usuario: <span className="font-mono">{addedPassenger.username}</span>. Su primera contraseña es su número de
            documento; el sistema le pide elegir una propia al ingresar. Entregale estos datos por un canal seguro.
          </Alert>
        </div>
      )}
      {isAdmin && members.data !== undefined && (
        <div className="mb-4 flex flex-wrap gap-3">
          <Button
            variant={hasSigner ? 'secondary' : 'primary'}
            onClick={() => {
              setRegistering(true);
            }}
          >
            {hasSigner ? 'Reemplazar el firmante por una persona nueva' : 'Registrar el firmante'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setAddedPassenger(undefined);
              setAddingPassenger(true);
            }}
          >
            + Agregar pasajero
          </Button>
        </div>
      )}
      <Loaded query={members}>
        {(items) => (
          <DataList<Member>
            caption="Integrantes del grupo"
            rows={items.filter(matches)}
            rowKey={(m) => m.id}
            empty="Todavía no hay integrantes. Cargalos en Importaciones."
            columns={[
              { header: 'Nombre', cell: (m) => `${m.lastNames}, ${m.firstNames}` },
              {
                header: 'Rol',
                cell: (m) =>
                  m.role === 'PASSENGER' ? (
                    'Pasajero'
                  ) : m.role === 'COMPANION' ? (
                    'Acompañante'
                  ) : (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {m.isSigner ? <Badge tone="info">Firmante</Badge> : 'Apoderado'}
                    </span>
                  ),
              },
              { header: 'Sexo', cell: (m) => SEX_TEXT[m.sex] ?? m.sex },
              { header: 'Documento', cell: (m) => <span className="font-mono">…{m.documentHint}</span> },
              {
                header: 'Estado',
                // One state, three values (DR-408): Baja, Pendiente de activar (still signs in with the document), Activo.
                cell: (m) =>
                  m.status !== 'ACTIVE' ? (
                    <Badge tone="neutral">Baja</Badge>
                  ) : m.accountState === 'PENDING' ? (
                    <Badge tone="warning">Pendiente de activar</Badge>
                  ) : (
                    <Badge tone="success">Activo</Badge>
                  ),
              },
              {
                header: 'Acción',
                end: true,
                cell: (m) =>
                  m.status === 'ACTIVE' && canWrite ? (
                    <span className="flex flex-wrap justify-end gap-2">
                      {isAdmin && (
                        <Button
                          variant="secondary"
                          aria-label={`Editar los datos de ${m.firstNames} ${m.lastNames}`}
                          onClick={() => {
                            setEditing(m);
                          }}
                        >
                          Editar
                        </Button>
                      )}
                      {isAdmin && m.accountId !== null && (
                        <Button
                          variant="ghost"
                          aria-label={`Restablecer la contraseña de ${m.firstNames} ${m.lastNames}`}
                          onClick={() => {
                            setResetting(m);
                          }}
                        >
                          Restablecer contraseña
                        </Button>
                      )}
                      {isAdmin && m.role === 'APODERADO' && !m.isSigner && (
                        <Button
                          variant="secondary"
                          aria-label={`Designar firmante a ${m.firstNames} ${m.lastNames}`}
                          onClick={() => {
                            setDesignating(m);
                          }}
                        >
                          Designar firmante
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        aria-label={`Dar de baja a ${m.firstNames} ${m.lastNames}`}
                        onClick={() => {
                          setCancelling(m);
                        }}
                      >
                        Dar de baja
                      </Button>
                    </span>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        )}
      </Loaded>
      <ReasonDialog
        open={cancelling !== undefined}
        title="¿Dar de baja al integrante?"
        confirmLabel="Dar de baja"
        danger
        busy={busy}
        onConfirm={(reason) => {
          if (cancelling === undefined) return;
          void run(() => backoffice.cancelMember(cancelling.id, reason), 'Integrante dado de baja.', [
            ['members', groupId],
          ]).then((ok) => {
            if (ok) setCancelling(undefined);
          });
        }}
        onClose={() => {
          setCancelling(undefined);
        }}
      >
        <p>
          {cancelling?.firstNames} {cancelling?.lastNames} pierde su plaza y su acceso. No se puede deshacer desde acá.
        </p>
      </ReasonDialog>
      <EditMemberDialog
        member={editing}
        busy={busy}
        onSave={(changes, reason) => {
          if (editing === undefined) return;
          void run(() => backoffice.updateMember(editing.id, changes, reason), 'Datos guardados.', [
            ['members', groupId],
          ]).then((ok) => {
            if (ok) setEditing(undefined);
          });
        }}
        onClose={() => {
          setEditing(undefined);
        }}
      />
      <PasswordResetDialog
        target={resetting === undefined ? undefined : `${resetting.firstNames} ${resetting.lastNames}`}
        mustChange
        busy={busy}
        onConfirm={(password, reason) => {
          if (resetting?.accountId === null || resetting === undefined) return;
          const account = resetting.accountId;
          void run(() => backoffice.resetPassword(account, password, reason), 'Contraseña restablecida.').then((ok) => {
            if (ok) setResetting(undefined);
          });
        }}
        onClose={() => {
          setResetting(undefined);
        }}
      />
      <ReasonDialog
        open={designating !== undefined}
        title="¿Designar firmante del grupo?"
        confirmLabel="Designar"
        busy={busy}
        onConfirm={(reason) => {
          if (designating === undefined) return;
          void run(() => backoffice.designateSigner(designating.id, reason), 'Firmante designado.', [
            ['members', groupId],
          ]).then((ok) => {
            if (ok) setDesignating(undefined);
          });
        }}
        onClose={() => {
          setDesignating(undefined);
        }}
      >
        <p>
          {designating?.firstNames} {designating?.lastNames} será la única persona que puede confirmar la distribución
          del grupo. Si ya había un firmante, deja de serlo en el mismo momento.
        </p>
      </ReasonDialog>
      <AddPassengerDialog
        open={addingPassenger}
        groupId={groupId}
        defaultDocumentType={countryOfHost(window.location.hostname) === 'Paraguay' ? 'CI_PY' : 'RUT'}
        defaultReason="Alta manual por un administrador"
        onClose={() => {
          setAddingPassenger(false);
        }}
        onDone={(name, username) => {
          setAddingPassenger(false);
          setAddedPassenger({ name, username });
          void queryClient.invalidateQueries({ queryKey: ['members', groupId] });
        }}
      />
      <NewSignerDialog
        open={registering}
        replacing={hasSigner}
        busy={busy}
        onSave={(person, reason) => {
          void run(
            async () => {
              const member = await backoffice.registerSigner(groupId, person, reason);
              setRegistered({ name: `${member.firstNames} ${member.lastNames}`, username: member.username });
            },
            'Firmante registrado.',
            [['members', groupId]],
          ).then((ok) => {
            if (ok) setRegistering(false);
          });
        }}
        onClose={() => {
          setRegistering(false);
        }}
      />
    </>
  );
}

const DOCUMENT_TYPES = [
  { value: 'RUT', label: 'RUT (Chile)' },
  { value: 'CI_PY', label: 'Cédula (Paraguay)' },
  { value: 'CI_UY', label: 'Cédula (Uruguay)' },
  { value: 'PASSPORT', label: 'Pasaporte' },
  { value: 'OTHER', label: 'Otro' },
];

/**
 * An administrator registers a NEW person as the group's signatory (prompt11 §4): the person and their account are
 * created and the signing responsibility passes to them in one step. For someone already in the list, "Designar
 * firmante" on their row is the way.
 */
function NewSignerDialog({
  open,
  replacing,
  busy,
  onSave,
  onClose,
}: {
  readonly open: boolean;
  readonly replacing: boolean;
  readonly busy: boolean;
  readonly onSave: (person: NewSigner, reason: string) => void;
  readonly onClose: () => void;
}) {
  const empty = { firstNames: '', lastNames: '', sex: 'UNDEFINED' as Member['sex'], type: '', number: '' };
  const [form, setForm] = useState(empty);
  const complete =
    form.firstNames.trim() !== '' && form.lastNames.trim() !== '' && form.type !== '' && form.number.trim() !== '';
  return (
    <ReasonDialog
      open={open}
      title={replacing ? 'Reemplazar el firmante' : 'Registrar el firmante'}
      confirmLabel={replacing ? 'Reemplazar' : 'Registrar'}
      busy={busy}
      disabled={!complete}
      onConfirm={(reason) => {
        onSave(
          {
            firstNames: form.firstNames.trim(),
            lastNames: form.lastNames.trim(),
            sex: form.sex,
            document: { type: form.type, number: form.number.trim() },
          },
          reason,
        );
        setForm(empty);
      }}
      onClose={() => {
        setForm(empty);
        onClose();
      }}
    >
      <p>
        {replacing
          ? 'La persona que informó el colegio pasa a ser la única que confirma la distribución. El firmante anterior deja de serlo en el mismo momento y queda como apoderado del grupo.'
          : 'La persona que informó el colegio será la única que confirma la distribución del grupo.'}{' '}
        Se le crea una cuenta: ingresa con su documento y elige su contraseña.
      </p>
      <TextField
        label="Nombres del firmante"
        maxLength={100}
        value={form.firstNames}
        onChange={(event) => {
          setForm({ ...form, firstNames: event.target.value });
        }}
      />
      <TextField
        label="Apellidos del firmante"
        maxLength={100}
        value={form.lastNames}
        onChange={(event) => {
          setForm({ ...form, lastNames: event.target.value });
        }}
      />
      <SelectField
        label="Tipo de documento"
        placeholder="Elegí el tipo"
        options={DOCUMENT_TYPES}
        value={form.type}
        onChange={(event) => {
          setForm({ ...form, type: event.target.value });
        }}
      />
      <TextField
        label="Número de documento"
        autoComplete="off"
        maxLength={30}
        value={form.number}
        onChange={(event) => {
          setForm({ ...form, number: event.target.value });
        }}
      />
      <SelectField
        label="Sexo del firmante"
        options={[
          { value: 'UNDEFINED', label: 'Sin definir' },
          { value: 'FEMALE', label: 'Mujer' },
          { value: 'MALE', label: 'Varón' },
        ]}
        value={form.sex}
        onChange={(event) => {
          setForm({ ...form, sex: event.target.value as Member['sex'] });
        }}
      />
    </ReasonDialog>
  );
}

/** An administrator corrects a member's data (prompt10 §15). Only the changed fields are sent; the username stays. */
function EditMemberDialog({
  member,
  busy,
  onSave,
  onClose,
}: {
  readonly member: Member | undefined;
  readonly busy: boolean;
  readonly onSave: (changes: MemberChanges, reason: string) => void;
  readonly onClose: () => void;
}) {
  const [form, setForm] = useState<{
    firstNames?: string;
    lastNames?: string;
    sex?: string;
    document?: string;
    birthDate?: string;
  }>({});
  const value = (field: 'firstNames' | 'lastNames' | 'sex'): string => form[field] ?? member?.[field] ?? '';
  const changes: MemberChanges = {
    ...(form.firstNames !== undefined && form.firstNames.trim() !== member?.firstNames
      ? { firstNames: form.firstNames.trim() }
      : {}),
    ...(form.lastNames !== undefined && form.lastNames.trim() !== member?.lastNames
      ? { lastNames: form.lastNames.trim() }
      : {}),
    ...(form.sex !== undefined && form.sex !== member?.sex ? { sex: form.sex as Member['sex'] } : {}),
    ...(form.document !== undefined && form.document.trim() !== ''
      ? { document: { type: member?.documentType ?? 'RUT', number: form.document.trim() } }
      : {}),
    ...(form.birthDate !== undefined ? { birthDate: form.birthDate.trim() === '' ? null : form.birthDate.trim() } : {}),
  };
  const close = (): void => {
    setForm({});
    onClose();
  };
  return (
    <ReasonDialog
      open={member !== undefined}
      title="Editar integrante"
      confirmLabel="Guardar"
      busy={busy}
      disabled={Object.keys(changes).length === 0}
      onConfirm={(reason) => {
        onSave(changes, reason);
        setForm({});
      }}
      onClose={close}
    >
      <TextField
        label="Nombres"
        value={value('firstNames')}
        onChange={(event) => {
          setForm({ ...form, firstNames: event.target.value });
        }}
      />
      <TextField
        label="Apellidos"
        value={value('lastNames')}
        onChange={(event) => {
          setForm({ ...form, lastNames: event.target.value });
        }}
      />
      <SelectField
        label="Sexo"
        options={[
          { value: 'FEMALE', label: 'Mujer' },
          { value: 'MALE', label: 'Varón' },
          { value: 'UNDEFINED', label: 'Sin definir' },
        ]}
        value={value('sex')}
        onChange={(event) => {
          setForm({ ...form, sex: event.target.value });
        }}
      />
      <TextField
        label="Corregir número de documento"
        hint={`Hoy termina en …${member?.documentHint ?? ''}. Dejalo vacío para no cambiarlo.`}
        autoComplete="off"
        value={form.document ?? ''}
        onChange={(event) => {
          setForm({ ...form, document: event.target.value });
        }}
      />
      <TextField
        label="Fecha de nacimiento (opcional)"
        hint={`${member?.hasBirthDate === true ? 'Ya hay una fecha cargada (no se muestra).' : 'Todavía no hay fecha cargada.'} Escribila como 25/12/2008 o 2008-12-25; sale en el rooming list. Vacío, para quitarla.`}
        placeholder="dd/mm/aaaa"
        autoComplete="off"
        value={form.birthDate ?? ''}
        onChange={(event) => {
          setForm({ ...form, birthDate: event.target.value });
        }}
      />
    </ReasonDialog>
  );
}

// ── Habitaciones ──

function RoomsTab({ stay }: { readonly stay: Stay }) {
  const { hasRole } = useSession();
  const editable = hasRole('TENANT_ADMIN', 'HOTEL_ADMIN') && ['DRAFT', 'OPEN', 'REOPENED'].includes(stay.status);
  // Retiring / replacing a room is the check-in emergency: possible while CONFIRMED too, until the stay is closed.
  const canReplace = hasRole('TENANT_ADMIN', 'HOTEL_ADMIN') && stay.status !== 'CLOSED';
  const [retiring, setRetiring] = useState<Room>();
  const rooms = useQuery({ queryKey: ['rooms', stay.id], queryFn: () => backoffice.rooms(stay.id) });
  const { busy, run } = useCommand();
  // The manual form (a free name), kept next to the presets (owner, 2026-10-07: both).
  const [form, setForm] = useState({
    label: '',
    capacity: '4',
    sexRestriction: 'FEMALE' as Room['sexRestriction'],
    count: '1',
  });
  // The kind of room being added (button pressed) and how many; null = no dialog.
  const [adding, setAdding] = useState<{ kind: RoomKind; sex: RoomSex }>();
  // Who the new rooms are for (owner, 2026-10-28): the students (default) or the companions, the parents who travel.
  const [audience, setAudience] = useState<RoomAudience>('PASSENGER');
  const [count, setCount] = useState('1');
  const keys = [
    ['rooms', stay.id],
    ['board', stay.id],
    ['distribution', stay.id],
  ] as const;
  return (
    <div className="flex flex-col gap-5">
      <Loaded query={rooms}>
        {(items) => (
          <>
            <p className="text-base text-muted">
              {items.length} habitaciones · {items.reduce((sum, r) => sum + r.capacity, 0)} plazas
            </p>
            <DataList<Room>
              caption="Habitaciones de la estadía"
              rows={items}
              rowKey={(r) => r.id}
              empty="La estadía todavía no tiene habitaciones."
              // prompt12 §2: rooms are added (several equal ones at once), never edited or deleted from here.
              columns={[
                { header: 'Habitación', cell: (r) => r.label },
                { header: 'Plazas', cell: (r) => r.capacity },
                {
                  header: 'Para',
                  cell: (r) =>
                    `${r.audience === 'COMPANION' ? 'Acompañantes · ' : 'Pasajeros · '}${ROOM_SEX_TEXT[r.sexRestriction]}`,
                },
                { header: 'Habitación del hotel', cell: (r) => r.physicalLabel ?? '—' },
                ...(canReplace
                  ? [
                      {
                        header: 'Acciones',
                        end: true,
                        cell: (r: Room) => (
                          <Button
                            variant="secondary"
                            size="sm"
                            aria-label={`Retirar o reemplazar la habitación ${r.label}`}
                            onClick={() => {
                              setRetiring(r);
                            }}
                          >
                            Retirar o reemplazar
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}
      </Loaded>
      {retiring !== undefined && (
        <RoomReplacementDialog
          stayId={stay.id}
          room={retiring}
          onClose={() => {
            setRetiring(undefined);
          }}
        />
      )}
      {editable && (
        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">Agregar habitaciones rápidas</h2>
          <p className="text-base text-muted">
            Tocá el tipo de habitación y elegí cuántas. Los nombres se ponen solos y se numeran («Doble Mujeres 1»,
            «Doble Mujeres 2»…). Una habitación creada no se edita ni se elimina; el número real del hotel se carga
            después (el hotel, o un administrador en Distribución).
          </p>
          <SelectField
            label="¿Para quién son estas habitaciones?"
            hint="Los acompañantes (los padres que viajan) tienen sus propias habitaciones: ellos ven solo las suyas y los pasajeros solo las de ellos. El firmante aprueba únicamente la distribución de pasajeros."
            options={[
              { value: 'PASSENGER', label: 'Pasajeros (los alumnos)' },
              { value: 'COMPANION', label: 'Acompañantes (los padres que viajan)' },
            ]}
            value={audience}
            onChange={(event) => {
              setAudience(event.target.value as RoomAudience);
            }}
          />
          {/* Mixed rooms do not exist at Funtour (owner, 2026-10-06): women and men always sleep apart. */}
          {ROOM_SEXES.map((sex) => (
            <div key={sex.value} role="group" aria-labelledby={`rooms-${sex.value}`} className="flex flex-col gap-2">
              <h3 id={`rooms-${sex.value}`} className="font-semibold">
                {sex.text}
              </h3>
              <div className="flex flex-wrap gap-2">
                {ROOM_KINDS.map((kind) => (
                  <Button
                    key={kind.code}
                    variant="secondary"
                    aria-label={`Agregar habitaciones ${kind.text} para ${sex.text}`}
                    onClick={() => {
                      setCount('1');
                      setAdding({ kind, sex: sex.value });
                    }}
                  >
                    {kind.text} <span className="text-sm text-muted">({kind.beds})</span>
                  </Button>
                ))}
              </div>
            </div>
          ))}
          {adding !== undefined && (
            <AddRoomsDialog
              kind={adding.kind}
              sex={adding.sex}
              existing={rooms.data ?? []}
              audience={audience}
              count={count}
              onCount={setCount}
              busy={busy}
              onConfirm={(list) => {
                void run(
                  () => backoffice.createRooms(stay.id, list),
                  list.length === 1 ? 'Habitación agregada.' : `${String(list.length)} habitaciones agregadas.`,
                  keys,
                ).then((ok) => {
                  if (ok) setAdding(undefined);
                });
              }}
              onClose={() => {
                setAdding(undefined);
              }}
            />
          )}
        </Card>
      )}
      {editable && (
        <Card>
          <h2 className="mb-3 text-lg font-semibold">Agregar una habitación a mano</h2>
          <p className="mb-3 text-base text-muted">
            Para un nombre propio que no está en los botones de arriba. Revisá los datos antes de agregar: una
            habitación creada no se edita ni se elimina. El número de habitación del hotel se carga después.
          </p>
          <form
            className="grid gap-4 md:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              // D-6: several equal rooms at once — "Nombre 1", "Nombre 2", … (one request: all or nothing).
              const count = Math.min(Math.max(Number(form.count) || 1, 1), 50);
              const base = { capacity: Number(form.capacity), sexRestriction: form.sexRestriction, audience };
              const list =
                count === 1
                  ? [{ label: form.label.trim(), ...base }]
                  : Array.from({ length: count }, (_, i) => ({
                      label: `${form.label.trim()} ${String(i + 1)}`,
                      ...base,
                    }));
              void run(
                () => backoffice.createRooms(stay.id, list),
                count === 1 ? 'Habitación agregada.' : `${String(count)} habitaciones agregadas.`,
                keys,
              ).then((ok) => {
                if (ok) setForm({ ...form, label: '', count: '1' });
              });
            }}
          >
            <TextField
              label="Nombre de la habitación (lo ven los pasajeros)"
              placeholder="Ej.: Single Fem, Suite Varones"
              hint="Es el nombre con el que los pasajeros eligen cama. No es el número del hotel: ese lo carga el hotel después."
              required
              maxLength={40}
              value={form.label}
              onChange={(event) => {
                setForm({ ...form, label: event.target.value });
              }}
            />
            <TextField
              label="Plazas"
              type="number"
              min={1}
              max={20}
              required
              value={form.capacity}
              onChange={(event) => {
                setForm({ ...form, capacity: event.target.value });
              }}
            />
            <SelectField
              label="Para"
              options={[
                // Mixed rooms do not exist at Funtour (owner, 2026-10-06): women and men always sleep apart.
                { value: 'FEMALE', label: 'Mujeres' },
                { value: 'MALE', label: 'Varones' },
              ]}
              value={form.sexRestriction}
              onChange={(event) => {
                setForm({ ...form, sexRestriction: event.target.value as Room['sexRestriction'] });
              }}
            />
            <TextField
              label="Cantidad de habitaciones iguales"
              type="number"
              min={1}
              max={50}
              hint="Con más de una se numeran: «Nombre 1», «Nombre 2»…"
              value={form.count}
              onChange={(event) => {
                setForm({ ...form, count: event.target.value });
              }}
            />
            <div className="md:col-span-3">
              <Button type="submit" loading={busy} disabled={form.label.trim() === ''}>
                Agregar habitación
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

// ── Room presets (owner, 2026-10-07): no free name — a kind, a sex and a number ──

type RoomSex = 'FEMALE' | 'MALE';
interface RoomKind {
  readonly code: string;
  readonly text: string;
  readonly beds: number;
}
const ROOM_KINDS: readonly RoomKind[] = [
  { code: 'SINGLE', text: 'Single', beds: 1 },
  { code: 'DOBLE', text: 'Doble', beds: 2 },
  { code: 'TRIPLE', text: 'Triple', beds: 3 },
  { code: 'CUADRUPLE', text: 'Cuádruple', beds: 4 },
  { code: 'QUINTUPLE', text: 'Quíntuple', beds: 5 },
];
const ROOM_SEXES: readonly { readonly value: RoomSex; readonly text: string }[] = [
  { value: 'FEMALE', text: 'Mujeres' },
  { value: 'MALE', text: 'Varones' },
];
const sexText = (sex: RoomSex): string => ROOM_SEX_TEXT[sex];

/** "Doble Mujeres 3", "Doble Mujeres 4"…: continues after the highest number already used for that kind and sex. */
export function nextRoomLabels(
  kind: RoomKind,
  sex: RoomSex,
  existing: readonly { readonly label: string }[],
  count: number,
  audience: RoomAudience = 'PASSENGER',
): string[] {
  // A companions' room carries it in its name («Acompañantes Doble Mujeres 1»): names are unique within the stay.
  const prefix = `${audience === 'COMPANION' ? 'Acompañantes ' : ''}${kind.text} ${sexText(sex)}`;
  const used = existing
    .map((room) => (room.label.startsWith(`${prefix} `) ? Number(room.label.slice(prefix.length + 1)) : NaN))
    .filter((n) => Number.isInteger(n));
  const start = (used.length === 0 ? 0 : Math.max(...used)) + 1;
  return Array.from({ length: count }, (_, i) => `${prefix} ${String(start + i)}`);
}

function AddRoomsDialog({
  kind,
  sex,
  existing,
  audience,
  count,
  onCount,
  busy,
  onConfirm,
  onClose,
}: {
  readonly kind: RoomKind;
  readonly sex: RoomSex;
  readonly existing: readonly Room[];
  readonly audience: RoomAudience;
  readonly count: string;
  readonly onCount: (value: string) => void;
  readonly busy: boolean;
  readonly onConfirm: (
    list: {
      label: string;
      capacity: number;
      sexRestriction: Room['sexRestriction'];
      roomTypeCode: string;
      audience: RoomAudience;
    }[],
  ) => void;
  readonly onClose: () => void;
}) {
  const n = Number(count);
  const valid = Number.isInteger(n) && n >= 1 && n <= 50;
  const labels = valid ? nextRoomLabels(kind, sex, existing, n, audience) : [];
  return (
    <Dialog
      open
      title={`Habitaciones ${kind.text} · ${sexText(sex)} · ${audience === 'COMPANION' ? 'acompañantes' : 'pasajeros'}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={busy}
            disabled={!valid}
            onClick={() => {
              onConfirm(
                labels.map((label) => ({
                  label,
                  capacity: kind.beds,
                  sexRestriction: sex,
                  roomTypeCode: kind.code,
                  audience,
                })),
              );
            }}
          >
            {valid && n > 1 ? `Agregar ${String(n)} habitaciones` : 'Agregar habitación'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField
          label="¿Cuántas?"
          type="number"
          min={1}
          max={50}
          inputMode="numeric"
          value={count}
          error={valid ? undefined : 'Entre 1 y 50.'}
          onChange={(event) => {
            onCount(event.target.value);
          }}
        />
        <p className="text-base">
          {kind.beds === 1 ? '1 cama' : `${String(kind.beds)} camas`} cada una, para {sexText(sex).toLowerCase()}.
        </p>
        {labels.length > 0 && (
          <p className="text-sm text-muted">
            Se van a crear: {labels.length <= 6 ? labels.join(', ') : `${labels[0] ?? ''} … ${labels.at(-1) ?? ''}`}.
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ── Distribución ──

function DistributionTab({ stay, timeZone }: { readonly stay: Stay; readonly timeZone: string }) {
  const { hasRole } = useSession();
  return (
    <StayBoard
      stayId={stay.id}
      mode={hasRole('TENANT_ADMIN', 'HOTEL_ADMIN') ? 'manage' : 'read'}
      showProgress
      showHistory={hasRole('TENANT_ADMIN', 'HOTEL_ADMIN')}
      canReopen={hasRole('TENANT_ADMIN', 'HOTEL_ADMIN')}
      canExchange={hasRole('TENANT_ADMIN', 'HOTEL_ADMIN')}
      canExport={hasRole('TENANT_ADMIN', 'HOTEL_ADMIN')}
      timeZone={timeZone}
    />
  );
}

// ── Importaciones: paste or pick the CSV → preview → confirm (all or nothing) ──

function ImportsTab({ groupId }: { readonly groupId: string }) {
  const { hasRole } = useSession();
  const { busy, run } = useCommand();
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<ImportPreview>();
  const [result, setResult] = useState<ImportCommit>();
  // What happened with the ERP's Excel (owner, 2026-10-07): how many passengers were read, or why not.
  const [fromExcel, setFromExcel] = useState<{ ok: boolean; text: string }>();

  /** Text that is the ERP's «ficha operativa» (a CSV saved from Excel, or pasted): converted like the .xlsx. Anything else: as is. */
  function fichaOf(text: string): { csv: string } | 'error' | undefined {
    const converted = fichaTextToCsv(text);
    if (converted === undefined) return undefined;
    if (!converted.ok) {
      setFromExcel({ ok: false, text: converted.reason });
      return 'error';
    }
    setFromExcel({
      ok: true,
      text: `Planilla del ERP leída: ${String(converted.passengers)} pasajeros (${String(converted.withEmail)} con correo). Revisá y tocá «Validar».`,
    });
    return { csv: converted.csv };
  }

  async function readFile(file: File): Promise<void> {
    setFromExcel(undefined);
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      const text = await file.text();
      const ficha = fichaOf(text);
      if (ficha === 'error') return;
      setCsv(ficha?.csv ?? text);
      return;
    }
    try {
      const converted = erpReportToCsv(await readXlsxRows(new Uint8Array(await file.arrayBuffer())));
      if (!converted.ok) {
        setFromExcel({ ok: false, text: converted.reason });
        return;
      }
      setCsv(converted.csv);
      setFromExcel({
        ok: true,
        text: `Planilla del ERP leída: ${String(converted.passengers)} pasajeros (${String(converted.withEmail)} con correo). Revisá y tocá «Validar».`,
      });
    } catch (error) {
      setFromExcel({
        ok: false,
        text: `No se pudo leer el Excel: ${error instanceof XlsxError ? error.message : 'formato no reconocido'}.`,
      });
    }
  }

  if (!hasRole('TENANT_ADMIN', 'HOTEL_ADMIN'))
    return <Alert tone="info">Solo operadores y administradores pueden importar integrantes.</Alert>;

  return (
    <div className="flex flex-col gap-5">
      <Card className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">1. Cargar el archivo</h2>
        <p className="text-base text-muted">
          CSV con las columnas <span className="font-mono">nombres; apellidos; numero_documento; sexo</span> y{' '}
          <span className="font-mono">rol</span> (pasajero / firmante / apoderado), y opcionalmente{' '}
          <span className="font-mono">correo</span> (para los avisos por correo). El archivo debe traer al{' '}
          <strong>firmante</strong> del grupo si el grupo todavía no tiene uno. El archivo no se guarda.
        </p>
        <p className="text-base text-muted">
          <strong>¿Tu planilla tiene otros títulos</strong> (por ejemplo, la del ERP)? Se lee por <strong>orden</strong>
          , sin importar cómo se llamen: 1 nombres · 2 apellidos · 3 documento · 4 sexo · 5 rol (opcional) · 6 correo
          (opcional).
        </p>
        <div className="flex flex-col gap-1">
          <label htmlFor="csv-file" className="text-sm font-medium">
            Archivo (CSV, o el Excel «ficha operativa» del ERP tal como sale)
          </label>
          <input
            id="csv-file"
            type="file"
            accept=".csv,text/csv,.xlsx"
            className="min-h-11 w-full rounded-lg border border-line bg-surface px-3 py-2 text-base"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) void readFile(file);
            }}
          />
        </div>
        {fromExcel !== undefined && <Alert tone={fromExcel.ok ? 'success' : 'error'}>{fromExcel.text}</Alert>}
        <p className="text-sm text-muted">
          La «ficha operativa» (Excel o CSV) se lee en tu computadora: de cada persona se toman solo apellido, nombre,
          sexo, tipo y número de documento, fecha de nacimiento, correo y rol. El celular, la restricción alimentaria,
          el hotel, el lugar de subida y el cliente <strong>no se leen ni se guardan</strong>. En la columna{' '}
          <strong>Rol</strong>: vacío = pasajero, <strong>X</strong> = firmante (con su correo en la columna{' '}
          <strong>Correo</strong>) y <strong>A</strong> = acompañante. Si la planilla no trae al firmante, agregá una
          fila más con sus datos y una <strong>X</strong> en esa columna; si el grupo ya tiene uno, no la agregues.
        </p>
        <TextAreaField
          label="…o pegá el contenido"
          hint="Abrí la planilla (Excel o CSV), seleccioná desde la fila de títulos hasta el último pasajero, copiá (Ctrl+C) y pegá acá (Ctrl+V). La primera fila es la de los títulos; con otros títulos, las columnas van en este orden: nombres, apellidos, documento, sexo, rol, correo."
          placeholder={
            'nombres;apellidos;numero_documento;sexo;rol;correo\nAna;Alba;12345678-9;F;pasajero;ana@ejemplo.com'
          }
          rows={6}
          spellCheck={false}
          value={csv}
          onChange={(event) => {
            setCsv(event.target.value);
          }}
        />
        <div>
          <Button
            loading={busy}
            disabled={csv.trim() === ''}
            onClick={() => {
              setResult(undefined);
              setPreview(undefined);
              setFromExcel(undefined);
              // Pasted from the ficha (TAB-separated, with its own titles): converted here, before it reaches the server.
              const ficha = fichaOf(csv);
              if (ficha === 'error') return;
              const toSend = ficha?.csv ?? csv;
              if (ficha !== undefined) setCsv(toSend);
              void run(async () => {
                setPreview(await backoffice.previewImport(groupId, toSend));
              }, 'Archivo validado. Revisá la vista previa.');
            }}
          >
            Validar
          </Button>
        </div>
      </Card>

      {preview !== undefined && (
        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">2. Vista previa</h2>
          <p className="text-base" aria-live="polite">
            {preview.totals.total} filas · <strong>{preview.totals.create}</strong> nuevas ·{' '}
            <strong>{preview.totals.update}</strong> con datos actualizados · {preview.totals.unchanged} sin cambios ·{' '}
            <strong>{preview.totals.error}</strong> con errores
          </p>
          {preview.blockers.includes('falta_el_firmante') && (
            <Alert tone="error" title="El archivo no trae al firmante del grupo">
              Este grupo todavía no tiene firmante y un grupo no puede operar sin él. No se importó nada. Agregá al
              archivo una fila con el rol «firmante» (la persona que informó el colegio) y validalo de nuevo; o registrá
              primero al firmante en la pestaña Integrantes.
            </Alert>
          )}
          {preview.warnings.map((warning) => (
            <Alert key={warning} tone="warning">
              {warning}
            </Alert>
          ))}
          <DataList
            caption="Filas del archivo"
            rows={preview.rows}
            rowKey={(row) => String(row.rowNumber)}
            empty="El archivo no tiene filas."
            columns={[
              { header: 'Fila', cell: (row) => row.rowNumber },
              { header: 'Nombre', cell: (row) => `${row.lastNames ?? ''}, ${row.firstNames ?? ''}` },
              {
                header: 'Rol',
                cell: (row) =>
                  row.isSigner
                    ? 'Firmante'
                    : row.role === 'PASSENGER'
                      ? 'Pasajero'
                      : row.role === 'COMPANION'
                        ? 'Acompañante'
                        : row.role === 'APODERADO'
                          ? 'Apoderado'
                          : '—',
              },
              { header: 'Correo', cell: (row) => <span className="break-all">{row.email ?? '—'}</span> },
              {
                header: 'Usuario propuesto',
                cell: (row) => <span className="font-mono">{row.proposedUsername ?? '—'}</span>,
              },
              {
                header: 'Resultado',
                cell: (row) =>
                  row.outcome === 'ERROR' ? (
                    <span className="text-danger">Error: {row.issues.join(' ')}</span>
                  ) : row.outcome === 'CREATE' ? (
                    <Badge tone="success">Se crea</Badge>
                  ) : row.outcome === 'UPDATE' ? (
                    <Badge tone="info">Se actualiza</Badge>
                  ) : (
                    <Badge>Sin cambios</Badge>
                  ),
              },
            ]}
          />
          {preview.totals.error > 0 ? (
            <Alert tone="error">
              Corregí el archivo y validalo de nuevo: con una sola fila con error no se importa nada.
            </Alert>
          ) : preview.blockers.length > 0 ? null : (
            <div>
              <Button
                loading={busy}
                disabled={preview.totals.create + preview.totals.update === 0}
                onClick={() => {
                  void run(
                    async () => {
                      setResult(await backoffice.commitImport(preview.id));
                      setPreview(undefined);
                      setCsv('');
                    },
                    'Importación confirmada.',
                    [['members', groupId]],
                  );
                }}
              >
                Confirmar importación
              </Button>
            </div>
          )}
        </Card>
      )}

      {result !== undefined && (
        <Card className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">3. Cuentas creadas</h2>
          <p className="text-base text-muted">
            La primera contraseña de cada persona es su número de documento; el sistema le exige cambiarla al ingresar.
          </p>
          <DataList
            caption="Cuentas creadas"
            rows={result.created}
            rowKey={(created) => created.memberId}
            empty="No se crearon cuentas."
            columns={[
              {
                header: 'Rol',
                cell: (created) =>
                  created.role === 'PASSENGER'
                    ? 'Pasajero'
                    : created.role === 'COMPANION'
                      ? 'Acompañante'
                      : 'Firmante o apoderado',
              },
              { header: 'Usuario', cell: (created) => <span className="font-mono">{created.username ?? '—'}</span> },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
